import { Locale } from "@guestpilot/contracts";
import enCommon from "../locales/en/common.json";
import enErrors from "../locales/en/errors.json";
import enEmails from "../locales/en/emails.json";
import enExports from "../locales/en/exports.json";
import enUi from "../locales/en/ui.json";
import frCommon from "../locales/fr/common.json";
import frErrors from "../locales/fr/errors.json";
import frEmails from "../locales/fr/emails.json";
import frExports from "../locales/fr/exports.json";
import frUi from "../locales/fr/ui.json";
import deCommon from "../locales/de/common.json";
import deErrors from "../locales/de/errors.json";
import deEmails from "../locales/de/emails.json";
import deExports from "../locales/de/exports.json";
import deUi from "../locales/de/ui.json";
import esCommon from "../locales/es/common.json";
import esErrors from "../locales/es/errors.json";
import esEmails from "../locales/es/emails.json";
import esExports from "../locales/es/exports.json";
import esUi from "../locales/es/ui.json";

export type LocaleCode = (typeof Locale.values)[number];
export const NAMESPACES = ["common", "errors", "emails", "exports", "ui"] as const;
export type Namespace = (typeof NAMESPACES)[number];
export const DEFAULT_LOCALE: LocaleCode = "en";

type Dict = { [key: string]: string | Dict };
export const RESOURCES: Record<LocaleCode, Record<Namespace, Dict>> = {
  en: { common: enCommon, errors: enErrors, emails: enEmails, exports: enExports, ui: enUi },
  fr: { common: frCommon, errors: frErrors, emails: frEmails, exports: frExports, ui: frUi },
  de: { common: deCommon, errors: deErrors, emails: deEmails, exports: deExports, ui: deUi },
  es: { common: esCommon, errors: esErrors, emails: esEmails, exports: esExports, ui: esUi },
};

/** Resolve a Teams locale tag ("fr-FR", "de-CH") to a supported language; falls back to English (BR-234). */
export function resolveLocale(input?: string | null): LocaleCode {
  const base = (input ?? "").toLowerCase().split(/[-_]/)[0] ?? "";
  return (Locale.values as readonly string[]).includes(base) ? (base as LocaleCode) : DEFAULT_LOCALE;
}

function lookup(dict: Dict | undefined, key: string): string | undefined {
  let cur: string | Dict | undefined = dict;
  for (const part of key.split(".")) {
    if (cur === undefined || typeof cur === "string") return undefined;
    cur = cur[part];
  }
  return typeof cur === "string" ? cur : undefined;
}

/** `t("errors:forbidden")` with `{{param}}` interpolation; missing keys fall back to English, then to the key. */
export function createTranslator(locale: LocaleCode) {
  return function t(fullKey: string, params: Record<string, string | number> = {}): string {
    const [ns, ...rest] = fullKey.split(":");
    const key = rest.join(":");
    const namespace = (rest.length ? ns : "common") as Namespace;
    const k = rest.length ? key : (ns as string);
    const raw =
      lookup(RESOURCES[locale][namespace], k) ?? lookup(RESOURCES[DEFAULT_LOCALE][namespace], k) ?? fullKey;
    return raw.replace(/\{\{(\w+)\}\}/g, (_m, p: string) => String(params[p] ?? `{{${p}}}`));
  };
}
